package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.Composable
import com.iregained.site.components.sections.about.styles.*
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATIONS: Slow Drift for text, Slow Zoom for background
val HeroSlowDriftKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateY(40.px) }
    100.percent { Modifier.opacity(1).translateY(0.px) }
}

val HeroBgZoomKeyframes = Keyframes {
    0.percent { Modifier.scale(1f) }
    100.percent { Modifier.scale(1.1f) }
}

@Composable
fun AboutHeroSection(data: AboutHeroSectionData = defaultAboutHeroData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-header-section"

    if (data.headline.isNotBlank() || data.backgroundImagePath.isNotBlank()) {
        Box(
            modifier = AboutHeroContainerStyle.toModifier().id(targetId)
            .styleModifier { property("scroll-margin-top", "100px") }
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden), contentAlignment = Alignment.Center) {
            if (data.backgroundImagePath.isNotBlank()) {
                Image(
                    src = data.backgroundImagePath,
                    alt = "IRegained MyHand System Hardware and Software",
                    modifier = AboutHeroBackgroundStyle.toModifier().animation(
                        HeroBgZoomKeyframes.toAnimation(
                            duration = 20.s,
                            timingFunction = AnimationTimingFunction.Linear,
                            fillMode = AnimationFillMode.Forwards
                        )
                    )
                )
            }

            Box(modifier = AboutHeroOverlayStyle.toModifier())

            Column(
                modifier = AboutHeroContentColumnStyle.toModifier().animation(
                    HeroSlowDriftKeyframes.toAnimation(
                        duration = 1.5.s, timingFunction = AnimationTimingFunction.EaseOut
                    )
                ), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center
            ) {
                if (data.headline.isNotBlank()) {
                    Div(
                        attrs = AboutHeroHeadlineStyle.toModifier().toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = data.headline
                                onDispose {}
                            }
                        })
                }

                if (data.subHeadline.isNotBlank()) {
                    Div(
                        attrs = AboutHeroSubheadlineStyle.toModifier()
                            .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.subHeadline; onDispose {} } })
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR ABOUT HERO:
 * @field backgroundImagePath:
 * - Aspect Ratio: 16:9 (Landscape) or wider.
 * - Recommended Dimensions: 1920x1080px.
 * - Format: WebP or high-quality JPEG.
 * - Content: A macro, high-resolution shot showing the physical MyHand® device with a patient's hand secured, and the glowing tablet dashboard displaying real-time movement data.
 */
data class AboutHeroSectionData(
    val sectionId: String? = null,
    val backgroundImagePath: String = Res.Images.About.BANNER,
    val headline: String = "A helping hand to those who need one",
    val subHeadline: String = "Precision Engineering\n" + "+\n" + "Intelligent Algorithms\n" + "=\n" + "Neuroplastic Recovery\n"
)

val defaultAboutHeroData = AboutHeroSectionData()